import { createFileRoute } from "@tanstack/react-router";
import ArticlePoolFinishComparison from "@/pages/ArticlePoolFinishComparison";
import { buildHead } from "@/lib/seo";
import { FEATURED_IMAGE_POOL_FINISH_COMPARISON } from "@/data/blogData";
import { FAQS_POOL_FINISH_COMPARISON } from "@/data/poolFinishComparisonData";

const PATH = "/blog/pool-finish-comparison-south-florida";
const CANONICAL = `https://precisionpaverspools.com${PATH}`;

// BlogPosting schema. The `image` field is included only once a verified
// featured-image URL has been supplied, so the structured data never
// references an empty value.
const blogPosting: Record<string, unknown> = {
  "@context": "https://schema.org",
  "@type": "BlogPosting",
  headline:
    "South Florida Pool Finish Comparison: Plaster vs. Quartz vs. Pebble",
  description:
    "Compare plaster, quartz, Diamond Brite, pebble, and other pool finishes by texture, appearance, water color, maintenance, variation, and project fit.",
  datePublished: "2026-10-16",
  dateModified: "2026-10-16",
  author: {
    "@type": "Organization",
    name: "Precision Pavers & Pool Design",
  },
  publisher: {
    "@type": "Organization",
    name: "Precision Pavers & Pool Design",
    url: "https://precisionpaverspools.com/",
  },
  mainEntityOfPage: {
    "@type": "WebPage",
    "@id": CANONICAL,
  },
};

if (FEATURED_IMAGE_POOL_FINISH_COMPARISON) {
  blogPosting.image = FEATURED_IMAGE_POOL_FINISH_COMPARISON;
}

const jsonLd: Record<string, unknown>[] = [
  blogPosting,
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: "https://precisionpaverspools.com/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Blog",
        item: "https://precisionpaverspools.com/blog",
      },
      {
        "@type": "ListItem",
        position: 3,
        name: "South Florida Pool Finish Comparison: Plaster vs. Quartz vs. Pebble",
        item: CANONICAL,
      },
    ],
  },
];

// FAQPage schema is emitted only when visible FAQs exist, so the schema
// always exactly matches the on-page FAQ content. During the preparation
// phase the shared FAQ array is empty, so no FAQPage is emitted yet.
if (FAQS_POOL_FINISH_COMPARISON.length > 0) {
  jsonLd.push({
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQS_POOL_FINISH_COMPARISON.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
      },
    })),
  });
}

export const Route = createFileRoute(
  "/blog/pool-finish-comparison-south-florida",
)({
  head: () =>
    buildHead({
      title: "Pool Finish Comparison: Plaster vs. Quartz vs. Pebble",
      description:
        "Compare plaster, quartz, Diamond Brite, pebble, and other pool finishes by texture, appearance, water color, maintenance, variation, and project fit.",
      path: PATH,
      ogType: "article",
      ogImage: FEATURED_IMAGE_POOL_FINISH_COMPARISON || undefined,
      jsonLd,
    }),
  component: ArticlePoolFinishComparison,
});
